import classNames from 'classnames'
import _ from 'lodash'
import React from 'react'

import { UI2FileUploadField, IUI2FileUploadFieldProps } from '../ui2-file-upload-field/ui2-file-upload-field'
import {
  UI2PhotoCaptureFieldProps,
  UI2_PHOTO_CAPTURE_FIELD_DEFAULT_PROPS,
  isPhotoCaptureRequired,
  resolvePhotoCaptureBounds,
} from 'shared-libs/components/ui2/fields/ui2-photo-capture-field/ui2-photo-capture-field-props'
import {
  FileCountBounds,
  FileSetValue,
  getFilesetFiles,
} from 'shared-libs/components/ui2/fields/core-field/media-attachment-field-props'
import { UI2EmptyStateLauncher } from '../field-ui/empty-state-launcher/empty-state-launcher'
import { UI2PulseHighlight, IUI2PulseHighlightRef } from '../../behavior/pulse-highlight/pulse-highlight'
import { createPulseFanout } from 'shared-libs/components/ui2/behavior/pulse-fanout'

import { UI2Icon } from 'browser/components/ui2/display/ui2-icon/ui2-icon'
import { UI2IconNames } from 'shared-libs/components/ui2/icons/ui2-icon-names'
import './_ui2-photo-capture-field.scss'

const DEFAULT_DROP_ZONE_TEXT = 'Drop photos here, or'
const DEFAULT_BROWSE_BUTTON_TEXT = 'browse photos'
const DEFAULT_EMPTY_MESSAGE_MW = 'Tap to take a photo'
const DEFAULT_ADD_MORE_TEXT = 'Add Photos'
const DEFAULT_CAPTURE_HELP_TEXT = 'Take Photo'
const DEFAULT_EMPTY_READ_ONLY_TEXT = 'No images available'
const DEFAULT_PREFILLED_EDIT_TEXT = 'Add/Remove Photos'

export interface IUI2PhotoCaptureFieldProps
  extends UI2PhotoCaptureFieldProps,
    Omit<IUI2FileUploadFieldProps, keyof UI2PhotoCaptureFieldProps> {}

export class UI2PhotoCaptureField extends UI2FileUploadField {
  public static defaultProps: Partial<IUI2PhotoCaptureFieldProps> = {
    ...UI2_PHOTO_CAPTURE_FIELD_DEFAULT_PROPS,
    accept: ['images'],
    addMoreMessage: DEFAULT_ADD_MORE_TEXT,
    maxImageDimension: UI2FileUploadField.defaultProps.maxImageDimension,
    imageQuality: UI2FileUploadField.defaultProps.imageQuality,
  }

  // Keyed by absolute slot index, created on demand and read lazily by the
  // fan-out — the shell asks for the pulse target while building its props,
  // which is before the tiles that own these refs have rendered.
  private slotPulseRefs: Record<number, React.RefObject<IUI2PulseHighlightRef>> = {}

  /**
   * The shell pulses the outstanding slots, not the row. A ring around the
   * whole container lights up the photos already taken as well, which reads as
   * "all of this is wrong" when only the empty slots are.
   */
  private slotPulseFanout = createPulseFanout(
    () => Object.values(this.slotPulseRefs).map((ref) => ref.current),
  )

  private getSlotPulseRef(slotIndex: number): React.RefObject<IUI2PulseHighlightRef> {
    if (!this.slotPulseRefs[slotIndex]) {
      this.slotPulseRefs[slotIndex] = React.createRef<IUI2PulseHighlightRef>()
    }
    return this.slotPulseRefs[slotIndex]
  }

  protected getShellPulseRef(isMobileEmpty: boolean): React.RefObject<any> | undefined {
    // Derived from the count, not from what has rendered: props are built
    // before children, so a ref-array check would be empty on first paint.
    if (this.getRequiredSlotCount(this.getValue().length) > 0) {
      return this.slotPulseFanout as any
    }
    return super.getShellPulseRef(isMobileEmpty)
  }

  // ─── Photo wording ────────────────────────────────────────────────────────

  protected defaultDropZoneText(): string {
    return DEFAULT_DROP_ZONE_TEXT
  }

  protected defaultBrowseButtonText(): string {
    return DEFAULT_BROWSE_BUTTON_TEXT
  }

  protected defaultAddMoreText(): string {
    return DEFAULT_ADD_MORE_TEXT
  }

  protected defaultEmptyMessageMobile(): string {
    return DEFAULT_EMPTY_MESSAGE_MW
  }

  // Photo capture holds images, not files — the inherited
  // 'No files available' reads wrong under a photo field.
  protected defaultEmptyReadOnlyText(): string {
    return DEFAULT_EMPTY_READ_ONLY_TEXT
  }

  protected defaultPrefilledEditText(): string {
    return DEFAULT_PREFILLED_EDIT_TEXT
  }

  // ─── Count bounds ─────────────────────────────────────────────────────────
  // `guidedCaptureOptions` sets the minimum on its own, so every count read has
  // to go through the photo-specific resolver — including the static the
  // renderer calls to gate submit.

  protected getEffectiveBounds(): FileCountBounds {
    return resolvePhotoCaptureBounds(this.props as IUI2PhotoCaptureFieldProps)
  }

  protected isCountRequired(): boolean {
    return isPhotoCaptureRequired(this.props as IUI2PhotoCaptureFieldProps)
  }

  public static getCountErrors(value: FileSetValue | undefined, props: any): string[] | undefined {
    const { isReadOnly, isDisabled, isOptional } = props
    if (isReadOnly || isDisabled || isOptional) return undefined
    if (!isPhotoCaptureRequired(props)) return undefined
    const bounds = resolvePhotoCaptureBounds(props)
    const count = getFilesetFiles(value as any).length
    if (count < bounds.min) {
      return [`at least ${bounds.min} ${bounds.min === 1 ? 'photo' : 'photos'} required`]
    }
    if (bounds.max != null && count > bounds.max) {
      return [`no more than ${bounds.max} ${bounds.max === 1 ? 'photo' : 'photos'} allowed`]
    }
    return undefined
  }

  // ─── Photo mode body ──────────────────────────────────────────────────────

  /**
   * A field with more than one required slot renders those tiles rather than
   * an empty state — the per-slot labels (`multiCaptureHelpText`) are the
   * instructions, so a generic launcher would drop them.
   */
  protected usesMobileEmptyLauncher(isEmpty: boolean): boolean {
    return super.usesMobileEmptyLauncher(isEmpty) && this.getRequiredSlotCount(0) === 0
  }

  protected renderBody(): React.ReactNode {
    const files = this.getValue()
    if (_.isEmpty(files) && this.getRequiredSlotCount(0) === 0) return this.renderEmptyState()
    return this.renderChipList(files)
  }

  /**
   * Outstanding labelled slots. Only `minFiles > 1` gets tiles: a single
   * required photo is just a required field, and a lone dashed tile in place
   * of the drop zone reads as a broken empty state.
   */
  private getRequiredSlotCount(fileCount: number): number {
    const { isDisabled, isReadOnly } = this.props
    if (isDisabled || isReadOnly) return 0
    const { min, max } = this.getEffectiveBounds()
    if (min <= 1 || this.isAtMax(fileCount)) return 0
    const remaining = max == null ? Number.POSITIVE_INFINITY : Math.max(0, max - fileCount)
    return Math.max(0, Math.min(min - fileCount, remaining))
  }

  // Photo captures are visual — uniform square thumbnails instead of the
  // file-upload field's filename text chips. The row still goes through
  // `renderPopulatedBody`, so DW keeps drag-and-drop onto it (and the
  // drag-hover outline) exactly as the file-upload field has.
  protected renderChipList(files: any[]): React.ReactNode {
    const { guidedCaptureOptions } = this.props as IUI2PhotoCaptureFieldProps
    const canRemove = !this.props.isDisabled && !this.props.isReadOnly
    const requiredSlots = this.getRequiredSlotCount(files.length)
    // Guided capture asks for a fixed, named set of photos — there is no such
    // thing as "one more". The slot tiles are the only way to add, so an Add
    // button would offer an unnamed photo the flow has no step for.
    const showAddChip = !guidedCaptureOptions?.length
      && requiredSlots === 0
      && this.canAddMore(files.length)
    return this.renderPopulatedBody(
      <div className='ui2-photoCaptureField-thumbnailRow'>
        {files.map((file: any, index: number) => this.renderThumbnail(file, index, canRemove))}
        {_.times(requiredSlots, (slot) => this.renderRequiredSlot(files.length + slot, slot))}
        {showAddChip && this.renderAddChip()}
      </div>,
      files.length,
    )
  }

  private renderThumbnail(file: any, index: number, canRemove: boolean): React.ReactNode {
    const { isDisabled } = this.props
    const uri = file?.uri || _.get(file, 'source[0].uri') || file?.source?.uri
    const key = file?.uniqueId || `${file?.name || 'photo'}-${index}`
    // A filled slot keeps its name. Without it a row of thumbnails says nothing
    // about which slot each one satisfied, and the labels the placeholders
    // carried disappear exactly when the user might want to check their work.
    const slotLabel = this.getSlotLabel(index)
    // Same affordance the file-upload field gives a new chip: a one-shot ring
    // so a photo that just came back from the camera is identifiable in a row
    // of otherwise similar tiles.
    const isNew = !!file?.uniqueId && this.state.recentlyAddedIds.has(file.uniqueId)
    return (
      <div key={key} className='ui2-photoCaptureField-cell'>
        <div className={classNames('ui2-photoCaptureField-thumbnail', {
          'ui2-photoCaptureField-thumbnail--new': isNew,
        })}>
          <button
            type='button'
            className='ui2-photoCaptureField-thumbnailTouchable'
            disabled={!!isDisabled}
            onClick={() => this.handleChipClick(index)}
            aria-label={slotLabel || file?.name || `Photo ${index + 1}`}
          >
            {uri ? (
              <img src={uri} alt={file?.name || ''} />
            ) : (
              <span className='ui2-photoCaptureField-thumbnailPlaceholder'>
                <UI2Icon name={UI2IconNames.IMAGE} size={20} />
              </span>
            )}
          </button>
          {canRemove && (
            <button
              type='button'
              className='ui2-photoCaptureField-thumbnailRemove'
              onClick={(e) => {
                e.stopPropagation()
                this.handleRemoveAtIndex(index)
              }}
              aria-label={`Remove ${slotLabel || file?.name || 'photo'}`}
            >
              <UI2Icon name={UI2IconNames.X} size={10} />
            </button>
          )}
        </div>
        {slotLabel && (
          <span className='ui2-photoCaptureField-cellLabel' title={slotLabel}>{slotLabel}</span>
        )}
      </div>
    )
  }

  /** The guided-capture label for the photo at `index`, if the schema named one. */
  private getSlotLabel(index: number): string | undefined {
    const { guidedCaptureOptions } = this.props as IUI2PhotoCaptureFieldProps
    return guidedCaptureOptions?.[index]?.label
  }

  /**
   * Titles the lightbox with the slot's name. Matched by identity rather than
   * by index: the lightbox indexes the *image subset*, which is only the same
   * list as the value because this field accepts images exclusively.
   */
  protected getLightboxCaption(current: any): string | undefined {
    const { guidedCaptureOptions } = this.props as IUI2PhotoCaptureFieldProps
    if (!guidedCaptureOptions?.length) return undefined
    const index = this.getValue().findIndex((file: any) => (
      file === current || (!!file?.uniqueId && file.uniqueId === current?.uniqueId)
    ))
    return index >= 0 ? guidedCaptureOptions[index]?.label : undefined
  }

  /** `slotIndex` is the absolute slot position — that's what indexes `guidedCaptureOptions`. */
  private renderRequiredSlot(slotIndex: number, key: number): React.ReactNode {
    const { guidedCaptureOptions } = this.props as IUI2PhotoCaptureFieldProps
    const label = guidedCaptureOptions?.[slotIndex]?.label ?? DEFAULT_CAPTURE_HELP_TEXT
    // Same cell wrapper as a filled thumbnail, with the label in the same place,
    // so a row of mixed filled/empty slots stays aligned and a tile keeps its
    // caption in the same spot before and after it is filled.
    return (
      <div key={`required-${key}`} className='ui2-photoCaptureField-cell'>
        <UI2PulseHighlight ref={this.getSlotPulseRef(slotIndex)} borderRadius={4}>
          <button
            type='button'
            className='ui2-photoCaptureField-requiredSlot'
            onClick={this.handleOpenPicker}
            aria-label={label}
          >
            <UI2Icon name={UI2IconNames.CAMERA} size={24} />
          </button>
        </UI2PulseHighlight>
        <span className='ui2-photoCaptureField-cellLabel' title={label}>{label}</span>
      </div>
    )
  }

  // Camera icon instead of the base's upload icon; everything else is the
  // inherited launcher.
  protected renderEmptyStateMobile(): React.ReactNode {
    const { placeholder, isDisabled } = this.props
    const message = placeholder || this.defaultEmptyMessageMobile()
    return (
      <>
        <UI2PulseHighlight ref={this.pulseRef}>
          <UI2EmptyStateLauncher
            onPress={this.handleOpenPicker}
            isDisabled={!!isDisabled}
            message={message}
            icon={<UI2Icon name={UI2IconNames.CAMERA} size={16} />}
          />
        </UI2PulseHighlight>
        {this.renderHiddenInput()}
      </>
    )
  }
}
